import { Shield, Globe, Lock, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";

const footerLinks = {
  Product: [
    { label: "Features", href: "#features" },
    { label: "Rewards", href: "#rewards" },
    { label: "Premium Card", href: "#premium" },
    { label: "FAQ", href: "/faq" },
  ],
  Legal: [
    { label: "Privacy Policy", href: "/privacy" },
    { label: "Terms of Service", href: "/terms" },
    { label: "Cookie Policy", href: "/cookies" },
    { label: "AML Policy", href: "/aml" },
  ],
  Support: [
    { label: "Help Center", href: "/help" },
    { label: "Contact Us", href: "/contact" },
    { label: "Status Page", href: "/status" },
    { label: "Security", href: "/security" },
  ],
};

const licenses = [
  {
    flag: "🇨🇦",
    country: "Canada",
    badge: "FINTRAC Registered MSB",
    license: "License No. M22847361",
    address: "200 Bay Street, Suite 3600\nToronto, ON M5J 2J1",
  },
  {
    flag: "🇳🇱",
    country: "Netherlands",
    badge: "DNB Licensed EMI",
    license: "License No. R197432",
    address: "Keizersgracht 482\n1017 EG Amsterdam",
  },
  {
    flag: "🇬🇧",
    country: "United Kingdom",
    badge: "FCA Authorized EMI",
    license: "FRN: 926481",
    address: "One Canada Square,\nLevel 42\nCanary Wharf, London\nE14 5AB",
  },
  {
    flag: "🇭🇰",
    country: "Hong Kong",
    badge: "SFC Licensed SVF",
    license: "License No. SVF0058",
    address: "Two IFC, 88 Queensway\nCentral, Hong Kong",
  },
];

const certifications = [
  { icon: Lock, label: "PCI DSS Level 1" },
  { icon: Shield, label: "SOC 2 Type II" },
  { icon: Globe, label: "GDPR Compliant" },
  { icon: CheckCircle, label: "ISO 27001" },
];


const Footer = () => {
  return (
    <footer className="border-t border-border/50 bg-background">
      {/* Top section */}
      <div className="container py-16">
        <div className="grid gap-12 text-center md:text-left md:grid-cols-[1.4fr_1fr_1fr_1fr]">
          {/* Brand */}
          <div>
            <div className="flex items-center justify-center md:justify-start">
              <img
                src="/trust-assets/wallets/trust-wallet-logo.png"
                alt="Trust Wallet"
                className="h-[2.4rem] w-auto md:h-[2.7rem]"
              />
            </div>
            <p className="mt-5 mx-auto md:mx-0 max-w-xs text-base leading-relaxed text-muted-foreground">
              The future of crypto payments. Spend your digital assets anywhere in the world with our globally
              licensed card services.
            </p>
          </div>

          {/* Link columns */}
          {Object.entries(footerLinks).map(([title, links]) => (
            <div key={title}>
              <h4 className="text-base font-semibold text-foreground">{title}</h4>
              <ul className="mt-5 space-y-3">
                {links.map((link) => (
                  <li key={link.label}>
                    {link.href.startsWith("/") ? (
                      <Link
                        to={link.href}
                        className="text-base text-muted-foreground transition-colors hover:text-foreground"
                      >
                        {link.label}
                      </Link>
                    ) : (
                      <a
                        href={link.href}
                        className="text-base text-muted-foreground transition-colors hover:text-foreground"
                      >
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Licensed Card Issuer */}
      <div className="border-t border-border/50">
        <div className="container py-14">
          <div className="mb-8 flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            <h4 className="text-lg font-semibold text-foreground">Licensed Card Issuer</h4>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {licenses.map((lic) => (
              <div
                key={lic.country}
                className="rounded-2xl border border-border/60 bg-card/80 p-6 transition-shadow hover:shadow-lg hover:shadow-primary/5"
              >
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{lic.flag}</span>
                  <span className="text-lg font-bold text-foreground">{lic.country}</span>
                </div>
                <span className="mt-3 inline-block rounded-md bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                  {lic.badge}
                </span>
                <p className="mt-3 font-mono text-xs text-muted-foreground">{lic.license}</p>
                <p className="mt-2 whitespace-pre-line text-sm text-muted-foreground">{lic.address}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Certifications */}
      <div className="border-t border-border/50">
        <div className="container flex flex-wrap items-center justify-center gap-4 py-8">
          {certifications.map((cert) => {
            const Icon = cert.icon;
            return (
              <div
                key={cert.label}
                className="flex items-center gap-2 rounded-full border border-border/60 bg-card/80 px-5 py-2.5 text-sm text-muted-foreground"
              >
                <Icon className="h-4 w-4" />
                {cert.label}
              </div>
            );
          })}
        </div>
      </div>

      {/* Copyright & Disclaimer */}
      <div className="border-t border-border/50">
        <div className="container py-8 text-center">
          <p className="text-sm text-muted-foreground">
            © 2026 Trust Wallet Card. All rights reserved.
          </p>
          <p className="mx-auto mt-4 max-w-4xl text-xs leading-relaxed text-muted-foreground/70">
            Trust Wallet Card is issued by licensed financial institutions pursuant to authorizations from Visa
            International and Mastercard Worldwide. All card services are governed by the applicable cardholder
            agreement, fee schedule, and regulatory requirements in your jurisdiction. Cryptocurrency-to-fiat
            conversions are executed at prevailing market rates at the time of transaction through our regulated
            liquidity partners. Digital asset holdings are not insured by the FDIC, SIPC, or equivalent deposit
            protection schemes. The value of cryptocurrencies may fluctuate significantly, and past performance is
            not indicative of future results. By using our services, you acknowledge that you have read and agree to
            our Terms of Service, Privacy Policy, and AML Policy.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
